import { Component, OnInit, ViewChild } from '@angular/core';
import { formatUtcDate } from '../../shared/date-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CmdbSyncService, SyncHistory, SyncHistoryItem } from '../../services/cmdb-sync.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { LeftMenuComponent } from '../../menu/left-menu/left-menu.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { SyncItemLogsSkeletonComponent } from './sync-item-logs-skeleton/sync-item-logs-skeleton.component';
import { SyncItemLogsTableSkeletonComponent } from './sync-item-logs-table-skeleton/sync-item-logs-table-skeleton.component';

@Component({
  selector: 'app-sync-item-logs',
  templateUrl: './sync-item-logs.component.html',
  styleUrls: ['./sync-item-logs.component.scss'],
  imports: [CommonModule, FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, SyncItemLogsSkeletonComponent, SyncItemLogsTableSkeletonComponent]
})
export class SyncItemLogsComponent implements OnInit {
  syncId: number = 0;
  syncHistory: SyncHistory | null = null;
  items: SyncHistoryItem[] = [];
  itemsTotal = 0;
  pageNumber = 1;
  pageSize = 50;
  pageLoading = false;

  loading = false;
  error: string | null = null;

  // Filters
  filterAppName = '';
  filterAction = '';

  private filterTextSubject = new Subject<string>();
  private initialPageRequestsPending = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cmdbSyncService: CmdbSyncService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.syncId = +params['id'];
      if (this.syncId) {
        this.beginInitialPageLoad(2);
        this.loadSyncHistory(true);
        this.loadItems(1, true);
      }
    });

    this.filterTextSubject.pipe(
      debounceTime(600),
      distinctUntilChanged()
    ).subscribe(() => this.applyFilters());
  }

  private beginInitialPageLoad(requests: number): void {
    this.initialPageRequestsPending = requests;
    this.pageLoading = true;
    this.error = null;
  }

  private completeInitialPageLoad(): void {
    this.initialPageRequestsPending = Math.max(0, this.initialPageRequestsPending - 1);
    if (this.initialPageRequestsPending === 0) {
      this.pageLoading = false;
    }
  }

  loadSyncHistory(isInitialLoad = false): void {
    this.cmdbSyncService.getSyncHistoryById(this.syncId).subscribe({
      next: (h) => {
        this.syncHistory = h;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        }
      },
      error: (err) => {
        console.error('Failed to load sync history', err);
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        }
      }
    });
  }

  loadItems(page: number, isInitialLoad = false): void {
    this.pageNumber = page;
    if (!isInitialLoad) {
      this.loading = true;
    }

    this.cmdbSyncService.getSyncHistoryItems(
      this.syncId,
      page,
      this.pageSize,
      this.filterAppName || undefined,
      this.filterAction || undefined
    ).subscribe({
      next: (resp) => {
        this.items = resp.items || [];
        this.itemsTotal = resp.totalCount || 0;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        } else {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Failed to load sync items', err);
        this.error = 'Failed to load sync log items';
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        } else {
          this.loading = false;
        }
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/cmdb-sync']);
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.loadItems(event.pageIndex + 1);
  }

  onFilterTextChange(): void {
    this.filterTextSubject.next(this.filterAppName);
  }

  applyFilters(): void {
    this.pageNumber = 1;
    this.loadItems(1);
  }

  clearFilters(): void {
    this.filterAppName = '';
    this.filterAction = '';
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return this.filterAppName !== '' || this.filterAction !== '';
  }

  getActionBadgeClass(action: string): string {
    switch (action) {
      case 'Inserted': return 'badge-success';
      case 'Updated':  return 'badge-info';
      case 'Failed':   return 'badge-danger';
      default:         return 'badge-secondary';
    }
  }

  getActionIcon(action: string): string {
    switch (action) {
      case 'Inserted': return 'add_circle';
      case 'Updated':  return 'update';
      case 'Failed':   return 'error';
      default:         return 'help';
    }
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) return '-';
    return formatUtcDate(new Date(date), 'short');
  }

  formatDuration(h: SyncHistory | null): string {
    if (!h?.startedAt) return '-';
    const end = h.completedAt ? new Date(h.completedAt) : new Date();
    const diffMs = end.getTime() - new Date(h.startedAt).getTime();
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  getSyncTypeLabel(h: SyncHistory | null): string {
    if (!h) return '';
    return (h as any).syncType === 'OrionEnrichment' ? 'Orion Enrichment' : 'CMDB Sync';
  }
}
